import { useState, useEffect } from 'react'
import ButtonEl from '../helpers/ctas/Button'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import themeConfig from '../../../theme.config'

const NavLevel2 = (props) => {
  return (
    <ul
      data-type="megamenu-level-2"
      id={props.id}
      className={`bg-${themeConfig.settings.nav[props.type].active} ${props.active ? `active block max-lg:border-t-4 max-lg:border-${props.decorationColor}` : `inactive hidden`}`}
    >
      {props.child.children.map((subchild, idx) => {
        return !subchild?.disablePageLinkInMegaMenu ? (
          <li key={idx} data-level="2">
            <a
              href={subchild.url}
              target={subchild.target ?? '_self'}
              className={`
                font-normal 
                block 
                m-0 
                p-2 
                lg:py-3
                flex 
                justify-between 
                text-${themeConfig.settings.nav[props.type].base}-text
                hover:text-${themeConfig.settings.nav[props.type].active}-text
                border-b-4
                text-${themeConfig.settings.nav[props.type].base}-text
                hover:text-${themeConfig.settings.nav[props.type].active}-text
                border-transparent
                transition-all
                duration-300
                focus:-outline-offset-4
                hover:border-${props.decorationColor}
                
              `}
              tabIndex={`${props.active ? 0 : -1}`}
              aria-hidden={`${props.active ? false : true}`}
            >
              {subchild.label}
            </a>
          </li>
        ) : (
          <></>
        )
      })}
    </ul>
  )
}

const NavLevel1 = (props) => {
  const [active, setActive] = useState(false)
  const [subActive, setSubActive] = useState(-1)
  const [menuImage, setMenuImage] = useState('')
  const [menuImageAlt, setMenuImageAlt] = useState('')
  const [hasChildActive, setHasChildActive] = useState(false)
  const mmHeightDefault = 224
  const [megamenuHeight, setMegamenuHeight] = useState(null)

  const handleNavClick = ({ target }) => {
    let clickTarget = target.nodeName === 'BUTTON' ? target : target.closest('button')
    setActive((prevState) => {
      const newState = !prevState
      props.handleNavClick(clickTarget.dataset?.idx)
      handleMenuImageSwap(clickTarget)
      return newState
    })
  }

  const handleMegaMenuClick = ({ target }) => {
    let clickTarget = target.nodeName === 'BUTTON' ? target : target.closest('button')
    const idx = clickTarget.dataset.idx
    handleMenuImageSwap(clickTarget)
    let x
    setSubActive((prevState) => {
      x = idx !== prevState ? idx : -1
      setHasChildActive(x === idx)
      return x
    })
    const relatedMegaMenu = clickTarget.getAttribute('aria-controls')
    const targetedMenu = document.getElementById(relatedMegaMenu)
    setTimeout(() => {
      // we need to wait a tick for the height to be revealed to us.
      setMegamenuHeight(x > -1 ? { minHeight: `${targetedMenu.offsetHeight}px` } : {})
    }, 10)
  }

  useEffect(() => {
    if (!props.active) {
      // if we close the parent, close the active second level nav too.
      setSubActive(-1)
      setMegamenuHeight({})
    }
  }, [props.active])

  const handleMenuImageSwap = (target) => {
    const imgTargetParent = target.closest('div')
    const imgTarget = imgTargetParent.querySelector('[data-img]')
    if (!imgTarget) return

    if (imgTarget.dataset?.img !== menuImage) {
      setMenuImage(imgTarget.dataset?.img)
      setMenuImageAlt(imgTarget.dataset?.imgAlt)
    }
  }

  return (
    <li
      data-children="true"
      data-level="0"
      className={`${props.active && (props.menuActive === true || props.menuActive === undefined) ? 'active' : 'inactive'}`}
    >
      <div
        data-type="menu-wrapper-0"
        className={`ml-4
          ${
            props.active
              ? `max-lg:bg-${themeConfig.settings.nav[props.type].active} max-lg:text-${themeConfig.settings.nav[props.type].active}-text `
              : `max-lg:bg-${themeConfig.settings.nav[props.type].base} max-lg:text-${themeConfig.settings.nav[props.type].base}-text border-transparent`
          }
          ${props.active && !hasChildActive ? `border-${props.generateHighlight(props.navItem?.navigationAccentColour)}` : 'border-transparent'}
        `}
      >
        {!props.navItem?.disablePageLinkInMegaMenu ? (
          <>
            <a
              href={props.navItem?.url}
              target={props.navItem?.target ?? '_self'}
              data-img-alt={props.navItem?.image?.alt}
              data-img={props.navItem?.image?.src}
              className={`h-full flex items-center hover:underline hover:decoration-4 focus:-outline-offset-4 px-0 hover:decoration-${props.generateHighlight(props.navItem?.navigationAccentColour)} underline-offset-8 `}
            >
              {props.navItem?.label}
            </a>
            <ButtonEl
              theme="none"
              idx={props.idx}
              className={`items-center h-full px-2 scale-100 hover:scale-[1.25] focus:!-outline-offset-4 px-1 transition-[scale]`}
              ariaHasPopup="true"
              ariaControls={`megamenu-${props.idx}${props.stub ? `-${props.stub}` : ''}`}
              ariaExpanded={`${props.active ? 'true' : 'false'}`}
              onClick={handleNavClick}
              buttonType="default"
            >
              <ChevronDownIcon
                className={`item-level-0-icon text-current h-4 w-4 text-base-text text-sm transition-all duration-1000`}
              />
              <div className="sr-only">{`Expand menu for item ${props.navItem?.label}`}</div>
            </ButtonEl>
          </>
        ) : (
          <ButtonEl
            theme="none"
            idx={props.idx}
            className={`h-full w-full pr-2 py-3 flex items-center hover:underline focus:!-outline-offset-4 hover:decoration-4 hover:decoration-${props.generateHighlight(props.navItem?.navigationAccentColour)} underline-offset-8 font-normal`}
            ariaHasPopup="true"
            ariaControls={`megamenu-${props.idx}${props.stub ? `-${props.stub}` : ''}`}
            ariaExpanded={`${props.active ? 'true' : 'false'}`}
            onClick={handleNavClick}
            buttonType="default"
          >
            <span
              data-img-alt={props.navItem?.image?.alt}
              data-img={props.navItem?.image?.src}
              className="font-normal inline-block mr-4"
            >
              {props.navItem?.label}
            </span>
            <ChevronDownIcon
              className={`item-level-0-icon text-current h-4 w-4 text-base-text ml-auto text-sm transition-all duration-1000`}
            />
            <div className="sr-only">{`Expand menu for item ${props.navItem?.label}`}</div>
          </ButtonEl>
        )}
      </div>
      <div
        data-type="megamenu-panel"
        id={`megamenu-${props.idx}${props.stub ? `-${props.stub}` : ''}`}
        style={megamenuHeight}
        className={`bg-${themeConfig.settings.nav[props.type].active} lg:shadow-md lg:bg-${themeConfig.settings.nav[props.type].base}`}
      >
        <ul data-type="megamenu-level-1">
          {props.navItem?.children.map((child, idx) => {
            return (
              <li
                key={idx}
                data-idx={idx}
                data-sub={subActive?.toString()}
                className={`
                  ${subActive?.toString() === idx.toString() ? `active max-lg:bg-${themeConfig.settings.nav[props.type].active}` : `inactive`} 
                  lg:text-${themeConfig.settings.nav[props.type].base}-text 
                  border-transparent 
                  hover:bg-${themeConfig.settings.nav[props.type].active} 
                  hover:border-${props.decorationColor}
                  hover:text-${themeConfig.settings.nav[props.type].active}-text
                `}
              >
                {(!child.children || !child.children?.length > 0) && !child?.disablePageLinkInMegaMenu && (
                  <>
                    <a
                      href={child.url}
                      data-idx={idx}
                      data-children="false"
                      target={child.target ?? '_self'}
                      className={`lg:py-3 w-full text-${themeConfig.settings.nav[props.type].base}-text lg:text-${themeConfig.settings.nav[props.type].base}-text hover:lg:bg-${themeConfig.settings.nav[props.type].active} hover:lg:text-${themeConfig.settings.nav[props.type].active}-text `}
                      tabIndex={`${props.active ? 0 : -1}`}
                      aria-hidden={`${props.active ? false : true}`}
                    >
                      {child.label}
                    </a>
                  </>
                )}
                {child.children && child.children?.length > 0 && (
                  <>
                    <div
                      data-type="menu-wrapper-1"
                      className={`text-${themeConfig.settings.nav[props.type].active}-text  `}
                    >
                      {!child?.disablePageLinkInMegaMenu ? (
                        <>
                          <a
                            href={child.url}
                            data-idx={idx}
                            data-children="true"
                            target={child.target ?? '_self'}
                            tabIndex={`${props.active ? 0 : -1}`}
                            aria-hidden={`${props.active ? false : true}`}
                            data-img-alt={child.image?.alt}
                            data-img={child.image?.src}
                            className={`lg:py-3 w-full hover:decoration-${props.decorationColor}`}
                          >
                            {child.label}
                          </a>
                          <ButtonEl
                            theme="none"
                            idx={idx}
                            buttonType="default"
                            className={`items-center px-2 `}
                            ariaHasPopup="true"
                            ariaControls={`megamenu-lv2-${props.idx ? props.idx : ''}-${idx}${props.stub ? `-${props.stub}` : ''}`}
                            ariaExpanded={`${subActive?.toString() === idx.toString() ? 'true' : 'false'}`}
                            ariaHidden={`${props.active ? 'false' : 'true'}`}
                            tabIndex={`${props.active ? 0 : -1}`}
                            onClick={handleMegaMenuClick}
                          >
                            <ChevronRightIcon
                              className={`max-lg:rotate-90 item-level-1-icon shrink-0 grow-0 h-4 w-4 text-current text-sm transition-all duration-1000`}
                            />
                            <div className="sr-only">{`Expand submenu for item ${child?.label}`}</div>
                          </ButtonEl>
                        </>
                      ) : (
                        <ButtonEl
                          theme="none"
                          idx={idx}
                          className={`items-center pl-8 ml-2 xl:ml-0 xl:pl-5 pr-2 lg:pr-2 py-3 w-full hover:decoration-${props.decorationColor} `}
                          ariaHasPopup="true"
                          ariaControls={`megamenu-lv2-${props.idx ? props.idx : ''}-${idx}${props.stub ? `-${props.stub}` : ''}`}
                          ariaExpanded={`${subActive?.toString() === idx.toString() ? 'true' : 'false'}`}
                          ariaHidden={`${props.active ? 'false' : 'true'}`}
                          tabIndex={`${props.active ? 0 : -1}`}
                          onClick={handleMegaMenuClick}
                          buttonType="default"
                        >
                          <span
                            data-img-alt={child.image?.alt}
                            data-img={child.image?.src}
                            className="font-normal inline-block mr-4"
                          >
                            {child.label}
                          </span>
                          <ChevronRightIcon
                            className={`item-level-1-icon max-lg:rotate-90 text-current h-4 w-4  ml-auto text-current text-sm transition-all duration-1000`}
                          />
                          <div className="sr-only">{`Expand menu for item ${child.label}`}</div>
                        </ButtonEl>
                      )}
                    </div>
                    <NavLevel2
                      {...props}
                      id={`megamenu-lv2-${props.idx ? props.idx : ''}-${idx}${props.stub ? `-${props.stub}` : ''}`}
                      setHasChildActive={setHasChildActive}
                      data-x={props.stub}
                      active={subActive?.toString() === idx.toString() ? true : false}
                      child={child}
                    />
                  </>
                )}
              </li>
            )
          })}
        </ul>
        {props.type && props.type === 'desktop' && (
          <div
            data-type="menu-decorator"
            className={`${subActive?.toString() !== '-1' ? `bg-${themeConfig.settings.nav[props.type].active}` : `bg-${themeConfig.settings.nav[props.type].base}`} `}
          >
            {menuImage && (
              <div>
                <img src={menuImage} alt={menuImageAlt} />
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

const MainNav = (props) => {
  const [activeIndex, setActiveIndex] = useState(null)
  const useMenuAccents = themeConfig.settings.useCMSMenuAccents ?? false
  const themeHighlight = themeConfig.settings.nav.desktop.highlight

  // test the settings to see if we want to use CMS-based link highlights.
  // if we do not, use the colour in the settings
  // if we do, test to see if it is set
  // if it is, use that colour, or fall back to the settings colour.
  const baseHighlightColour = useMenuAccents
    ? props.res?.navigationAccentColour?.length > 1
      ? `${props.res?.navigationAccentColour.toLowerCase()}-500`
      : `${themeHighlight}-500`
    : `${themeHighlight}-500`

  const setActive = (isActive) => {
    // global menu active should be the same as local menu active
    props.setMenuActive(isActive)
    props.setSearchActive((prevState) => {
      let newState = prevState
      if (isActive === true) {
        // is menu has become active turn the search off
        return false
      }
      // otherwise do nothing.
      props.setCtasActive(newState)

      return newState
    })
  }

  const handleNavClick = (idx = null) => {
    if (!idx) {
      return false
    }
    setActiveIndex((prevState) => {
      let isActive = true

      if (idx === prevState) {
        isActive = false
      }
      setActive(isActive)
      return isActive ? idx : null
    })
  }

  const generateHighlight = (colour) =>
    colour && colour?.length ? `${colour.toLowerCase()}-500` : `${baseHighlightColour}`

  document.addEventListener('keydown', (evt) => {
    if (evt.key !== 'Escape') return
    props.setMenuActive(false)
    setActiveIndex(null)
  })

  useEffect(() => {
    if (props.type === 'desktop') {
      document.addEventListener('click', (evt) => {
        if (
          !evt.target.closest('[data-type="desktop-navigation"]') &&
          !evt.target.closest('[data-type="mobile-navigation"]')
        ) {
          props.setMenuActive(false)
          setActiveIndex(null)
        }
      })
    }
  }, [])

  return (
    <nav data-type={`${props.type}-navigation`}>
      <ul data-type="menu-level-0">
        {props.nav?.map((navItem, idx) => {
          return navItem.children && navItem.children?.length ? (
            <NavLevel1
              key={idx}
              {...props}
              handleNavClick={handleNavClick}
              active={activeIndex && idx.toString() === activeIndex.toString() ? true : false}
              idx={idx}
              navItem={navItem}
              themeHighlight={themeHighlight}
              baseHighlightColour={baseHighlightColour}
              decorationColor={`${generateHighlight(navItem?.navigationAccentColour)}`}
              generateHighlight={generateHighlight}
            />
          ) : !navItem?.disablePageLinkInMegaMenu ? (
            <li key={idx} data-children="false">
              <a
                className={`h-full flex items-center hover:underline focus:-outline-offset-4 hover:decoration-4 hover:decoration-${generateHighlight(navItem?.navigationAccentColour)} underline-offset-8 `}
                href={navItem?.url}
                target={navItem?.target}
              >
                {navItem?.label}
              </a>
            </li>
          ) : (
            <></>
          )
        })}
      </ul>
    </nav>
  )
}

export default MainNav
